// @givmo/board — the year's questions, answered: the ONE form both roads fill (CBG-YR2-WEB1's paper
// form, CBG-YR2-WEB2's own questionnaire).
//
// ══ WHAT THE FORM KEEPS, AND WHAT IT NEVER DOES ═════════════════════════════════════════════════
//
//   * THE QUESTIONS ARE THE SERVER'S. The form renders exactly the questions the read lists for this
//     person (`I-5` to a director only; the statement only while a policy is shown), with their words
//     as asked; it never adds, drops or re-words one, so what is sent is what the backend asks for.
//   * LAST YEAR'S ANSWERS COME BACK FILLED IN, with no tap of their own (Jonathan's answer to question
//     4): a carried answer is signed as it is unless changed. A carried entry keeps its KEY, the
//     server's own, so the redline, the register's "New since" and a link to it name the same
//     interest year after year; a new entry carries none, and the server mints one.
//   * THE STATEMENT IS TICKED FRESH, never carried: it starts unticked, and nothing waits on it (the
//     orchestrator's call, 2026-10-12): the answers record whether it was ticked.
//   * A CHANGE IS SAID IN WORDS ("Last year: No", "Added", "Removed"), each in a block marked by a
//     leading rule, never a fill (`coi-parts.tsx`).
//
// Nothing here tells anyone what an answer means.

import { useMemo, useState, type ReactNode } from 'react';
import { FileText, Plus, Undo2 } from 'lucide-react';
import type {
  BoardCoiAnswerIn,
  BoardCoiEntryIn,
  BoardCoiEntryOut,
  BoardCoiPreviousOut,
  BoardCoiQuestionAskedOut,
} from '@givmo/api-client';
import {
  Card,
  CardBody,
  Checkbox,
  ErrorMessage,
  Icon,
  RadioTile,
  RadioTileGroup,
  TextField,
  Textarea,
  cn,
} from '@givmo/design-system';
import { FieldNote } from '../../components/field-note';
import { HowThisWorks } from '../../components/fine-print';
import { displayDate } from '../../lib/display';
import {
  ENTRY_FIELDS,
  KIND_ACKNOWLEDGMENT,
  KIND_ENTRIES,
  KIND_TEXT,
  PART_OTHER,
  PART_POLICY,
  answerWord,
  type EntryField,
  type EntryLike,
} from './coi-decode';
import { DependentBlock, EntryLine, PartLabel, StatementText } from './coi-parts';
import { coiWords as w } from './coi-words';

const BODY = 'font-sans text-s15 font-w400 leading-h15 tracking-givmo text-text-strong';
const LINK =
  'font-sans text-s14 font-w600 tracking-givmo text-brand-600 outline-none hover:text-brand-700 focus-visible:underline';

// ── The state ─────────────────────────────────────────────────────────────────────────────────────

/** One entry as typed or carried: its server key (null for a new one) and its fields. */
export interface EntryDraft {
  /** A stable id for this screen only. */
  localId: string;
  key: string | null;
  fields: Partial<Record<EntryField, string>>;
}

/** One answer as it stands on the screen. */
export interface AnswerDraft {
  /** The statement's tick; `"yes"` / `"no"` for a yes-or-no or entries question; the words for text. */
  value: boolean | string | null;
  entries: EntryDraft[];
  /** Entries carried from last year and taken out this year, kept so Undo can put them back. */
  removed: EntryDraft[];
  details: string;
  /** An entry open for typing. */
  editing: string | null;
}

export type AnswerDrafts = Record<string, AnswerDraft>;

let localSequence = 0;
function nextLocalId(): string {
  localSequence += 1;
  return `entry-${localSequence}`;
}

function entryFromWire(entry: BoardCoiEntryOut): EntryDraft {
  const fields: Partial<Record<EntryField, string>> = {};
  for (const field of [...ENTRY_FIELDS, 'details'] as const) {
    const value = entry[field];
    if (typeof value === 'string' && value.length > 0) fields[field] = value;
  }
  return { localId: nextLocalId(), key: entry.key, fields };
}

function blankEntry(): EntryDraft {
  return { localId: nextLocalId(), key: null, fields: {} };
}

/** Last year's answer to one question, as the read serves it, or null. */
export function previousAnswerOf(
  previous: BoardCoiPreviousOut | null | undefined,
  questionId: string,
) {
  return previous?.answers.find((answer) => answer.question_id === questionId) ?? null;
}

/**
 * The form's starting state: last year's answers filled in (Q4), the statement unticked (it is ticked
 * fresh), or — for a correction — the answers being corrected, every one as recorded.
 */
export function initialDrafts(
  questions: readonly BoardCoiQuestionAskedOut[],
  previous: BoardCoiPreviousOut | null | undefined,
  correcting?: readonly {
    question_id: string;
    value?: unknown;
    entries?: BoardCoiEntryOut[];
    details?: string | null;
  }[],
): AnswerDrafts {
  const drafts: AnswerDrafts = {};
  for (const question of questions) {
    const source = correcting
      ? (correcting.find((answer) => answer.question_id === question.id) ?? null)
      : previousAnswerOf(previous, question.id);
    let value: boolean | string | null = null;
    if (question.kind === KIND_ACKNOWLEDGMENT) {
      // Ticked fresh every year; a correction starts from the tick it corrects.
      value = correcting ? source?.value === true : false;
    } else if (question.kind === KIND_TEXT) {
      value = typeof source?.value === 'string' ? source.value : '';
    } else if (source?.value === 'yes' || source?.value === 'no') {
      value = source.value;
    }
    drafts[question.id] = {
      value,
      entries: (source?.entries ?? []).map(entryFromWire),
      removed: [],
      details: typeof source?.details === 'string' ? source.details : '',
      editing: null,
    };
  }
  return drafts;
}

/** The identifying field of a question's entries (`name`, or `person` for relationships). */
function identifyingField(question: BoardCoiQuestionAskedOut): { key: EntryField; label: string } {
  const required = (question.fields ?? []).find((field) => field.required);
  return {
    key: ((required?.key as EntryField | undefined) ?? 'name') as EntryField,
    label: required?.label ?? '',
  };
}

/** What still stands between the form and Review: each question answered, each entry named. */
export function draftProblems(
  questions: readonly BoardCoiQuestionAskedOut[],
  drafts: AnswerDrafts,
): Record<string, string> {
  const problems: Record<string, string> = {};
  for (const question of questions) {
    const draft = drafts[question.id];
    if (draft === undefined) continue;
    if (question.kind === KIND_ACKNOWLEDGMENT || question.kind === KIND_TEXT) continue;
    if (draft.value !== 'yes' && draft.value !== 'no') {
      problems[question.id] = w.answerEach;
      continue;
    }
    if (question.kind === KIND_ENTRIES && draft.value === 'yes') {
      const { key, label } = identifyingField(question);
      const named = draft.entries.filter((entry) => (entry.fields[key] ?? '').trim().length > 0);
      if (draft.entries.length === 0 || named.length !== draft.entries.length)
        problems[question.id] = w.nameEach(label);
    }
  }
  return problems;
}

/** The answers as the backend takes them: exactly the questions listed, one answer each. */
export function answersIn(
  questions: readonly BoardCoiQuestionAskedOut[],
  drafts: AnswerDrafts,
): BoardCoiAnswerIn[] {
  return questions.map((question) => {
    const draft = drafts[question.id];
    const answer: BoardCoiAnswerIn = { question_id: question.id };
    if (question.kind === KIND_ACKNOWLEDGMENT) {
      answer.value = draft?.value === true;
      return answer;
    }
    if (question.kind === KIND_TEXT) {
      answer.value = typeof draft?.value === 'string' ? draft.value.trim() : '';
      return answer;
    }
    answer.value = draft?.value === 'yes' ? 'yes' : 'no';
    if (question.kind === KIND_ENTRIES && draft?.value === 'yes') {
      answer.entries = draft.entries.map((entry) => {
        const sent: BoardCoiEntryIn = entry.key === null ? {} : { key: entry.key };
        for (const [field, value] of Object.entries(entry.fields)) {
          const trimmed = (value ?? '').trim();
          if (trimmed.length > 0) sent[field as EntryField] = trimmed;
        }
        return sent;
      });
    }
    const details = draft?.details.trim() ?? '';
    if (details.length > 0) answer.details = details;
    return answer;
  });
}

/** Whether an entry's fields are what last year's same entry said. */
function sameEntry(entry: EntryDraft, before: BoardCoiEntryOut | undefined): boolean {
  if (before === undefined) return false;
  return [...ENTRY_FIELDS, 'details' as const].every(
    (field) => (entry.fields[field] ?? '').trim() === (before[field] ?? '').trim(),
  );
}

/** Whether one answer differs from last year's (the review's count, the marks on a card). */
export function answerChanged(
  question: BoardCoiQuestionAskedOut,
  draft: AnswerDraft | undefined,
  previous: BoardCoiPreviousOut | null | undefined,
): boolean {
  if (draft === undefined || question.kind === KIND_ACKNOWLEDGMENT) return false;
  const before = previousAnswerOf(previous, question.id);
  if (before === null) return false;
  if (question.kind === KIND_TEXT)
    return (
      (typeof draft.value === 'string' ? draft.value.trim() : '') !==
      String(before.value ?? '').trim()
    );
  if (draft.value !== before.value) return true;
  if ((draft.details ?? '').trim() !== (before.details ?? '').trim()) return true;
  const beforeEntries = before.entries ?? [];
  if (draft.entries.length !== beforeEntries.length) return true;
  return draft.entries.some(
    (entry) =>
      !sameEntry(
        entry,
        beforeEntries.find((candidate) => candidate.key === entry.key),
      ),
  );
}

/** The state, and the edits a form makes to it. */
export function useAnswerDrafts(initial: () => AnswerDrafts) {
  const [drafts, setDrafts] = useState<AnswerDrafts>(initial);
  const edit = (questionId: string, change: (draft: AnswerDraft) => AnswerDraft) =>
    setDrafts((current) => {
      const draft = current[questionId];
      return draft === undefined ? current : { ...current, [questionId]: change(draft) };
    });
  return { drafts, setDrafts, edit };
}

// ── The form ──────────────────────────────────────────────────────────────────────────────────────

export interface AnswerFormProps {
  questions: readonly BoardCoiQuestionAskedOut[];
  previous: BoardCoiPreviousOut | null | undefined;
  drafts: AnswerDrafts;
  edit: (questionId: string, change: (draft: AnswerDraft) => AnswerDraft) => void;
  /** The policy shown this year, or null; the statement is asked only with one. */
  policy: { title: string; adopted_on?: string | null } | null;
  /** The year's label, for "you tick it for 2027". */
  label: string;
  /** The period's words, for "during 2026". */
  period: string;
  /** "Read it", where the reader can open the policy (the person's own form). */
  readPolicy?: ReactNode;
  /** Problems to show on their questions once the form tried to go on. */
  problems: Record<string, string>;
  /** Whether last year's answers came back filled in (the marks and the statement's note). */
  carried: boolean;
  /**
   * Every yes-or-no answer offers its note: a correction, which compares nothing with last year, so
   * the note the person signed stays theirs to change.
   */
  detailsAlways?: boolean;
  /**
   * An officer copying a person's signed paper form: the statement is theirs, in the words the form
   * prints, so it is named as theirs (`lead`) and its tick records what the form shows (`tick`),
   * never the officer's own confirmation. Absent on the person's own questionnaire.
   */
  paperStatement?: { lead: string; tick: string };
  testId: string;
}

export function AnswerForm({
  questions,
  previous,
  drafts,
  edit,
  policy,
  label,
  period,
  readPolicy,
  problems,
  carried,
  detailsAlways = false,
  paperStatement,
  testId,
}: AnswerFormProps) {
  const statement = questions.find((question) => question.part === PART_POLICY) ?? null;
  const interests = questions.filter(
    (question) => question.part !== PART_POLICY && question.part !== PART_OTHER,
  );
  const other = questions.filter((question) => question.part === PART_OTHER);

  return (
    <div className="flex flex-col gap-24" data-testid={testId}>
      {statement !== null ? (
        <section className="flex flex-col gap-12" data-testid={`${testId}-policy`}>
          <PartLabel>{w.boardsPolicy}</PartLabel>
          <Card>
            <CardBody className="flex flex-col gap-14">
              {policy !== null ? (
                <div className="flex flex-wrap items-center justify-between gap-8">
                  <span className="flex items-center gap-10">
                    <Icon icon={FileText} size={18} aria-hidden className="text-icon-muted" />
                    <span className="flex flex-col">
                      <span className="font-sans text-s15 font-w600 tracking-givmo text-text-strong">
                        {policy.title}
                      </span>
                      {policy.adopted_on ? (
                        <span className="font-sans text-s13 font-w400 tracking-givmo text-text-tertiary">
                          {w.adopted(displayDate(policy.adopted_on))}
                        </span>
                      ) : null}
                    </span>
                  </span>
                  {readPolicy}
                </div>
              ) : null}
              {paperStatement === undefined ? (
                <div className="flex items-start gap-12">
                  <Checkbox
                    appearance="saas"
                    checked={drafts[statement.id]?.value === true}
                    onCheckedChange={(checked) =>
                      edit(statement.id, (draft) => ({ ...draft, value: checked === true }))
                    }
                    aria-label={[
                      statement.text_as_asked,
                      ...(statement.points_as_asked ?? []),
                    ].join(' ')}
                    className="mt-2"
                    testId={`${testId}-statement`}
                  />
                  <StatementText
                    lead={statement.text_as_asked}
                    points={statement.points_as_asked ?? []}
                    className={BODY}
                  />
                </div>
              ) : (
                <div className="flex flex-col gap-10">
                  <p
                    className="font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary"
                    data-testid={`${testId}-statement-lead`}
                  >
                    {paperStatement.lead}
                  </p>
                  <StatementText
                    lead={statement.text_as_asked}
                    points={statement.points_as_asked ?? []}
                    className={BODY}
                  />
                  <label className="flex items-center gap-10 font-sans text-s14 font-w400 tracking-givmo text-text-strong">
                    <Checkbox
                      appearance="saas"
                      checked={drafts[statement.id]?.value === true}
                      onCheckedChange={(checked) =>
                        edit(statement.id, (draft) => ({ ...draft, value: checked === true }))
                      }
                      testId={`${testId}-statement`}
                    />
                    {paperStatement.tick}
                  </label>
                </div>
              )}
              {carried ? (
                <FieldNote testId={`${testId}-statement-note`}>{w.notCarried(label)}</FieldNote>
              ) : null}
            </CardBody>
          </Card>
        </section>
      ) : null}

      {interests.length > 0 ? (
        <section className="flex flex-col gap-12" data-testid={`${testId}-interests`}>
          <PartLabel>{w.interestsDuring(period)}</PartLabel>
          {interests.map((question, index) => (
            <QuestionCard
              key={question.id}
              number={index + 1}
              question={question}
              draft={drafts[question.id]}
              previous={carried ? previousAnswerOf(previous, question.id) : null}
              edit={edit}
              detailsAlways={detailsAlways}
              problem={problems[question.id] ?? null}
              testId={`${testId}-${question.id}`}
            />
          ))}
        </section>
      ) : null}

      {other.map((question) => (
        <section key={question.id} className="flex flex-col gap-12" data-testid={`${testId}-other`}>
          <PartLabel>{w.anythingElse}</PartLabel>
          <Card>
            <CardBody className="flex flex-col gap-10">
              <span className={BODY}>{question.text_as_asked}</span>
              <Textarea
                label={w.optional}
                value={
                  typeof drafts[question.id]?.value === 'string'
                    ? (drafts[question.id]?.value as string)
                    : ''
                }
                onChange={(event) =>
                  edit(question.id, (draft) => ({ ...draft, value: event.target.value }))
                }
                rows={3}
                testId={`${testId}-${question.id}-text`}
              />
            </CardBody>
          </Card>
        </section>
      ))}
    </div>
  );
}

/** One question: its words, "What counts", Yes or No, and its entries with what moved since last year. */
function QuestionCard({
  number,
  question,
  draft,
  previous,
  edit,
  detailsAlways,
  problem,
  testId,
}: {
  number: number;
  question: BoardCoiQuestionAskedOut;
  draft: AnswerDraft | undefined;
  previous: ReturnType<typeof previousAnswerOf>;
  edit: AnswerFormProps['edit'];
  detailsAlways: boolean;
  problem: string | null;
  testId: string;
}) {
  if (draft === undefined) return null;
  const entriesQuestion = question.kind === KIND_ENTRIES;
  const beforeValue = previous?.value;
  const valueChanged =
    previous !== null && beforeValue !== undefined && beforeValue !== draft.value;
  const added = draft.entries.filter((entry) => entry.key === null);
  const carriedEntries = draft.entries.filter((entry) => entry.key !== null);
  const showMarks = previous !== null;
  const detailsChanged =
    previous !== null && draft.details.trim() !== (previous.details ?? '').trim();
  // A changed answer offers its note (d3-rolled's question 2), except a Yes whose entries carry their
  // own details (its question 1); a note already written always shows.
  const entriesCarryDetails = entriesQuestion && draft.value === 'yes';
  const showDetails =
    (detailsAlways && typeof draft.value === 'string') ||
    draft.details.trim().length > 0 ||
    (previous !== null && (detailsChanged || (valueChanged && !entriesCarryDetails)));

  const choose = (value: string) =>
    edit(question.id, (current) => {
      if (value === 'no') {
        // "No" names no entries: carried ones move to "Removed" (Undo puts them back), new ones go.
        return {
          ...current,
          value,
          removed: [...current.removed, ...current.entries.filter((entry) => entry.key !== null)],
          entries: [],
          editing: null,
        };
      }
      if (entriesQuestion && current.entries.length === 0 && current.removed.length === 0) {
        // Yes with nothing to name yet: one blank entry, open for typing (d2-fresh).
        const first = blankEntry();
        return { ...current, value, entries: [first], editing: first.localId };
      }
      if (entriesQuestion && current.entries.length === 0 && current.removed.length > 0) {
        // Back to Yes: last year's entries return, as they were.
        return { ...current, value, entries: current.removed, removed: [], editing: null };
      }
      return { ...current, value };
    });

  return (
    <Card testId={testId}>
      <CardBody className="flex flex-col gap-12">
        <p className={BODY}>
          <span className="font-w600 tabular-nums text-text-tertiary">{number}. </span>
          {question.text_as_asked}
        </p>
        {(question.help ?? []).length > 0 ? (
          <div className="flex">
            <HowThisWorks
              testId={`${testId}-help`}
              label={w.whatCounts}
              lines={question.help ?? []}
            />
          </div>
        ) : null}
        <RadioTileGroup
          value={typeof draft.value === 'string' ? draft.value : ''}
          onValueChange={choose}
          className="flex flex-wrap gap-x-32 gap-y-8"
          testId={`${testId}-answer`}
        >
          <RadioTile value="yes" label={w.yes} />
          <RadioTile value="no" label={w.no} />
        </RadioTileGroup>
        {showMarks && valueChanged ? (
          <p
            className="font-sans text-s13 font-w400 tracking-givmo text-text-tertiary"
            data-testid={`${testId}-last`}
          >
            {w.lastYear(answerWord(beforeValue, question.kind))}
          </p>
        ) : null}

        {entriesQuestion && draft.value === 'yes' ? (
          <EntriesEditor
            question={question}
            draft={draft}
            carriedEntries={showMarks ? carriedEntries : draft.entries}
            addedEntries={showMarks ? added : []}
            edit={edit}
            testId={testId}
          />
        ) : null}

        {showMarks && draft.removed.length > 0 ? (
          <DependentBlock
            label={w.removed}
            testId={`${testId}-removed`}
            aside={
              <button
                type="button"
                className={cn(LINK, 'inline-flex items-center gap-4 text-s13')}
                onClick={() =>
                  edit(question.id, (current) => ({
                    ...current,
                    value: 'yes',
                    entries: [...current.removed, ...current.entries],
                    removed: [],
                  }))
                }
                data-testid={`${testId}-undo`}
              >
                <Icon icon={Undo2} size={14} aria-hidden />
                {w.undo}
              </button>
            }
          >
            {draft.removed.map((entry) => (
              <EntryLine key={entry.localId} entry={entry.fields as EntryLike} mark="del" />
            ))}
          </DependentBlock>
        ) : null}

        {showDetails ? (
          <Textarea
            label={w.anythingToAdd}
            value={draft.details}
            onChange={(event) =>
              edit(question.id, (current) => ({ ...current, details: event.target.value }))
            }
            rows={2}
            testId={`${testId}-details`}
          />
        ) : null}

        {problem !== null ? (
          <ErrorMessage testId={`${testId}-problem`}>{problem}</ErrorMessage>
        ) : null}
      </CardBody>
    </Card>
  );
}

/** The entries of a Yes: each as a line (Edit · Remove) or open for typing, and "Add another". */
function EntriesEditor({
  question,
  draft,
  carriedEntries,
  addedEntries,
  edit,
  testId,
}: {
  question: BoardCoiQuestionAskedOut;
  draft: AnswerDraft;
  carriedEntries: EntryDraft[];
  addedEntries: EntryDraft[];
  edit: AnswerFormProps['edit'];
  testId: string;
}) {
  const fields = (question.fields ?? []).filter((field) => field.key !== 'details');
  const detailsField = (question.fields ?? []).find((field) => field.key === 'details');

  const setField = (localId: string, field: string, value: string) =>
    edit(question.id, (current) => ({
      ...current,
      entries: current.entries.map((entry) =>
        entry.localId === localId
          ? { ...entry, fields: { ...entry.fields, [field]: value } }
          : entry,
      ),
    }));
  const remove = (entry: EntryDraft) =>
    edit(question.id, (current) => ({
      ...current,
      entries: current.entries.filter((candidate) => candidate.localId !== entry.localId),
      removed: entry.key === null ? current.removed : [...current.removed, entry],
      editing: current.editing === entry.localId ? null : current.editing,
    }));
  const add = () =>
    edit(question.id, (current) => {
      const next = blankEntry();
      return { ...current, entries: [...current.entries, next], editing: next.localId };
    });

  const renderEntry = (entry: EntryDraft, mark: 'ins' | null) =>
    draft.editing === entry.localId ? (
      <div
        key={entry.localId}
        className="flex flex-col gap-12"
        data-testid={`${testId}-entry-editing`}
      >
        <div className="grid grid-cols-1 gap-12 sm:grid-cols-2">
          {fields.map((field) => (
            <TextField
              key={field.key}
              label={field.label}
              value={entry.fields[field.key as EntryField] ?? ''}
              onChange={(event) => setField(entry.localId, field.key, event.target.value)}
              testId={`${testId}-field-${field.key}`}
            />
          ))}
        </div>
        {detailsField ? (
          <Textarea
            label={detailsField.label}
            value={entry.fields.details ?? ''}
            onChange={(event) => setField(entry.localId, 'details', event.target.value)}
            rows={2}
            testId={`${testId}-field-details`}
          />
        ) : null}
      </div>
    ) : (
      <div
        key={entry.localId}
        className="flex flex-wrap items-start justify-between gap-x-12 gap-y-4"
        data-testid={`${testId}-entry`}
      >
        <div className="min-w-0 flex-1">
          <EntryLine entry={entry.fields as EntryLike} mark={mark} />
        </div>
        <span className="flex shrink-0 gap-12">
          <button
            type="button"
            className={cn(LINK, 'text-s13')}
            onClick={() => edit(question.id, (current) => ({ ...current, editing: entry.localId }))}
          >
            {w.edit}
          </button>
          <button type="button" className={cn(LINK, 'text-s13')} onClick={() => remove(entry)}>
            {w.remove}
          </button>
        </span>
      </div>
    );

  const addButton = (
    <button
      type="button"
      className={cn(LINK, 'inline-flex items-center gap-6 self-start text-s13')}
      onClick={add}
      data-testid={`${testId}-add`}
    >
      <Icon icon={Plus} size={14} aria-hidden />
      {w.addAnother}
    </button>
  );

  return (
    <>
      {carriedEntries.length > 0 || addedEntries.length === 0 ? (
        <div
          className={cn(
            'flex flex-col gap-10 border-l-2 pl-12',
            draft.editing !== null &&
              carriedEntries.some((entry) => entry.localId === draft.editing)
              ? 'border-brand-500'
              : 'border-border-strong',
          )}
          data-testid={`${testId}-entries`}
        >
          {carriedEntries.map((entry) => renderEntry(entry, null))}
          {addedEntries.length === 0 ? addButton : null}
        </div>
      ) : null}
      {addedEntries.length > 0 ? (
        <DependentBlock label={w.added} testId={`${testId}-added`}>
          {addedEntries.map((entry) => renderEntry(entry, 'ins'))}
          {addButton}
        </DependentBlock>
      ) : null}
    </>
  );
}

/** A small button row's spacer: the form's own submit sits under it. */
export function FormActions({ children }: { children: ReactNode }) {
  return (
    <div className="flex flex-wrap items-center justify-end gap-12 border-t border-border-subtle pt-16">
      {children}
    </div>
  );
}

/** For the review: the questions whose answers moved since last year, in order. */
export function useChangedQuestions(
  questions: readonly BoardCoiQuestionAskedOut[],
  drafts: AnswerDrafts,
  previous: BoardCoiPreviousOut | null | undefined,
) {
  return useMemo(
    () => questions.filter((question) => answerChanged(question, drafts[question.id], previous)),
    [questions, drafts, previous],
  );
}
